import { useEffect, useRef } from 'react';

const DEFAULT_TIMEOUT = 1000 * 60; // 1 Minute

export const DEFAULT_EVENTS = [
    'click',
    'keydown',
    'scroll',
    'wheel',
    'mousemove',
    'mousedown',
    'mouseenter',
    'mousewheel',
    'touchmove',
    'touchstart',
];

function throttled(fn: Function) {
    let lastCall = 0;
    return function(...args: any) {
        const now = new Date().getTime();
        if (now - lastCall < 200) {
            return;
        }
        lastCall = now;
        return fn(...args);
    };
}

interface MouseActivityProps {
    timeout?: number;
    onInactive: () => void;
}

const useMouseActivity = ({ onInactive, timeout = DEFAULT_TIMEOUT }: MouseActivityProps) => {
    const timeoutId = useRef<NodeJS.Timeout>();
    const eventsBound = useRef(false);
    const inactive = useRef(false);
    const lastActive = useRef(0);
    const emitOnInactive = useRef(onInactive);

    const resetTimeout = () => {
        if (timeoutId.current) {
            clearTimeout(timeoutId.current);
            timeoutId.current = undefined;
        }
    };

    const _toggleInactiveState = () => {
        const nextIdle = !inactive.current;
        inactive.current = nextIdle;

        nextIdle ? emitOnInactive.current() : _bindEvents();
    };

    const _handleEvent = () => {
        resetTimeout();

        const elapsedTimeSinceLastActive = new Date().getTime() - lastActive.current;

        if (inactive.current || (!inactive.current && elapsedTimeSinceLastActive > timeout)) {
            _toggleInactiveState();
        }

        lastActive.current = new Date().getTime();

        if (!inactive.current) {
            timeoutId.current = setTimeout(_toggleInactiveState, timeout);
        }
    };

    const handleEvent = useRef(_handleEvent);

    const _bindEvents = () => {
        if (!eventsBound.current) {
            DEFAULT_EVENTS.forEach((e) => {
                window.addEventListener(e, handleEvent.current, {
                    capture: true,
                    passive: true,
                });
            });
            eventsBound.current = true;
        }
    };

    const _unbindEvents = () => {
        if (eventsBound.current) {
            DEFAULT_EVENTS.forEach((e) => {
                window.removeEventListener(e, handleEvent.current, { capture: true });
            });
            eventsBound.current = false;
        }
    };

    const start = () => {
        resetTimeout();
        _bindEvents();
        inactive.current = false;
        lastActive.current = new Date().getTime();
        timeoutId.current = setTimeout(_toggleInactiveState, timeout);
    };

    useEffect(() => {
        handleEvent.current = throttled(_handleEvent);
        start();

        return () => {
            timeoutId.current && clearTimeout(timeoutId.current);
            _unbindEvents();
        };
    }, []);

    useEffect(() => {
        emitOnInactive.current = onInactive;
    }, [onInactive]);

    const isInactive = () => inactive.current;

    const getLastActiveTime = () => lastActive.current;

    const getRemainingTime = () => {
        const timeLeft = timeout - (new Date().getTime() - lastActive.current);
        return timeLeft < 0 ? 0 : timeLeft;
    };

    return {
        isInactive,
        getLastActiveTime,
        getRemainingTime,
    };
};

export default useMouseActivity;
